import React, { useState } from 'react';
import { 
  Plus, Trash2, ChevronUp, ChevronDown,
  Type, List, Grid3X3, AlertTriangle, Columns, Heart, 
  HelpCircle, BarChart3, CheckCircle, FileText, Image
} from 'lucide-react';
import CMSImagePicker from './CMSImagePicker';

/**
 * Inline Structured Content Editor
 * Accordion-style editor for AI-generated structured JSON content
 */

// Section type icons and labels
const sectionTypes = {
  'text-block': { icon: Type, label: 'Text Block', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  'image-text': { icon: Image, label: 'Image + Text', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  'benefit-cards': { icon: Grid3X3, label: 'Benefit Cards', color: 'bg-green-100 text-green-700 border-green-200' },
  'symptom-cards': { icon: CheckCircle, label: 'Symptom Cards', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  'process-steps': { icon: List, label: 'Process Steps', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  'info-box': { icon: AlertTriangle, label: 'Info Box', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  'comparison-columns': { icon: Columns, label: 'Comparison', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  'care-cards': { icon: Heart, label: 'Care Cards', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  'summary-table': { icon: FileText, label: 'Summary Table', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  'feature-grid': { icon: Grid3X3, label: 'Feature Grid', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  'faq': { icon: HelpCircle, label: 'FAQ', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  'stats': { icon: BarChart3, label: 'Statistics', color: 'bg-red-100 text-red-700 border-red-200' },
  'bullet-list': { icon: List, label: 'Bullet List', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

// Available icons for cards
const availableIcons = [
  'shield', 'heart', 'trending-up', 'award', 'activity', 'clock',
  'check-circle', 'alert-triangle', 'target', 'stethoscope', 'star',
  'sparkles', 'zap', 'users', 'calendar', 'pill', 'syringe', 'thermometer'
];

// Shared input styles
const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const smallInputClass = "w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";

// Auto-expanding textarea component
const AutoTextarea = ({ value, onChange, placeholder, className = "", minRows = 2 }) => {
  const textareaRef = React.useRef(null);
  
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className={`${smallInputClass} resize-none overflow-hidden ${className}`}
      style={{ minHeight: `${minRows * 1.5}rem` }}
    />
  );
};

// Text Block Editor
const TextBlockEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Paragraphs</label>
      {(section.paragraphs || ['']).map((para, idx) => (
        <div key={idx} className="flex gap-2 mb-2">
          <AutoTextarea
            value={para}
            onChange={(e) => {
              const newParagraphs = [...(section.paragraphs || [''])];
              newParagraphs[idx] = e.target.value;
              onChange({ ...section, paragraphs: newParagraphs });
            }}
            placeholder="Enter paragraph text..."
            minRows={3}
          />
          {(section.paragraphs || []).length > 1 && (
            <button
              onClick={() => {
                const newParagraphs = (section.paragraphs || ['']).filter((_, i) => i !== idx);
                onChange({ ...section, paragraphs: newParagraphs });
              }}
              className="p-2 text-red-500 hover:bg-red-50 rounded self-start"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      <div className="flex items-center gap-4">
        <button
          onClick={() => onChange({ ...section, paragraphs: [...(section.paragraphs || []), ''] })}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Add Paragraph
        </button>
        <CMSImagePicker
          value={section.image || ''}
          onChange={(url) => onChange({ ...section, image: url })}
          showInline={true}
          buttonLabel="Add Image"
        />
      </div>
    </div>
    
    {/* Show image preview and position if image is set */}
    {section.image && (
      <div className="border-t border-gray-200 pt-4">
        <div className="flex gap-4 items-start">
          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image Position</label>
              <select
                value={section.imagePosition || 'right'}
                onChange={(e) => onChange({ ...section, imagePosition: e.target.value })}
                className={inputClass}
              >
                <option value="right">Image on Right</option>
                <option value="left">Image on Left</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image Aspect Ratio</label>
              <div className="flex gap-1">
                {['square', 'landscape', 'portrait'].map((aspect) => (
                  <button
                    key={aspect}
                    type="button"
                    onClick={() => onChange({ ...section, imageAspect: aspect })}
                    className={`px-3 py-1.5 text-xs rounded capitalize ${
                      (section.imageAspect || 'landscape') === aspect
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    {aspect}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="w-32">
            <img src={section.image} alt="Section" className="w-full h-20 object-cover rounded-lg border border-gray-200" />
            <button
              onClick={() => onChange({ ...section, image: '', imagePosition: 'right', imageAspect: 'landscape' })}
              className="text-xs text-red-600 hover:text-red-700 mt-1"
            >
              Remove Image
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Image + Text Editor
const ImageTextEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Content Paragraphs</label>
      {(section.content ? (Array.isArray(section.content) ? section.content : [section.content]) : ['']).map((para, idx) => (
        <div key={idx} className="flex gap-2 mb-2">
          <AutoTextarea
            value={para}
            onChange={(e) => {
              const currentContent = section.content 
                ? (Array.isArray(section.content) ? section.content : [section.content]) 
                : [''];
              const newContent = [...currentContent];
              newContent[idx] = e.target.value;
              onChange({ ...section, content: newContent });
            }}
            placeholder="Enter paragraph text..."
            minRows={3}
          />
          {(Array.isArray(section.content) ? section.content : [section.content || '']).length > 1 && (
            <button
              onClick={() => {
                const currentContent = Array.isArray(section.content) ? section.content : [section.content || ''];
                const newContent = currentContent.filter((_, i) => i !== idx);
                onChange({ ...section, content: newContent });
              }}
              className="p-2 text-red-500 hover:bg-red-50 rounded self-start"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={() => {
          const currentContent = section.content 
            ? (Array.isArray(section.content) ? section.content : [section.content]) 
            : [''];
          onChange({ ...section, content: [...currentContent, ''] });
        }}
        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Paragraph
      </button>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <CMSImagePicker
        value={section.image || ''}
        onChange={(url) => onChange({ ...section, image: url })}
        label="Image URL"
        placeholder="https://example.com/image.jpg"
        previewHeight="h-32"
      />
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image Alt Text</label>
          <input
            type="text"
            value={section.imageAlt || ''}
            onChange={(e) => onChange({ ...section, imageAlt: e.target.value })}
            className={inputClass}
            placeholder="Description for accessibility"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image Position</label>
          <select
            value={section.imagePosition || 'right'}
            onChange={(e) => onChange({ ...section, imagePosition: e.target.value })}
            className={inputClass}
          >
            <option value="right">Image on Right</option>
            <option value="left">Image on Left</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image Aspect Ratio</label>
          <div className="flex gap-1">
            {['square', 'landscape', 'portrait'].map((aspect) => (
              <button
                key={aspect}
                type="button"
                onClick={() => onChange({ ...section, imageAspect: aspect })}
                className={`px-3 py-1.5 text-xs rounded capitalize ${
                  (section.imageAspect || 'landscape') === aspect
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {aspect}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Bullet Points (Optional)</label>
      {(section.bullets || []).map((bullet, idx) => (
        <div key={idx} className="flex gap-2 items-center mb-2">
          <span className="text-gray-400">•</span>
          <input
            type="text"
            value={bullet || ''}
            onChange={(e) => {
              const newBullets = [...(section.bullets || [])];
              newBullets[idx] = e.target.value;
              onChange({ ...section, bullets: newBullets });
            }}
            className={`flex-1 ${smallInputClass}`}
            placeholder="Bullet point"
          />
          <button
            onClick={() => {
              const newBullets = (section.bullets || []).filter((_, i) => i !== idx);
              onChange({ ...section, bullets: newBullets });
            }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, bullets: [...(section.bullets || []), ''] })}
        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Bullet Point
      </button>
    </div>
  </div>
);

// Benefit Cards Editor
const BenefitCardsEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Cards</label>
      {(section.cards || []).map((card, idx) => (
        <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-600">Card {idx + 1}</span>
            <button
              onClick={() => {
                const newCards = section.cards.filter((_, i) => i !== idx);
                onChange({ ...section, cards: newCards });
              }}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Icon</label>
              <select
                value={card.icon || 'shield'}
                onChange={(e) => {
                  const newCards = [...section.cards];
                  newCards[idx] = { ...card, icon: e.target.value };
                  onChange({ ...section, cards: newCards });
                }}
                className={smallInputClass}
              >
                {availableIcons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Title</label>
              <input
                type="text"
                value={card.title || ''}
                onChange={(e) => {
                  const newCards = [...section.cards];
                  newCards[idx] = { ...card, title: e.target.value };
                  onChange({ ...section, cards: newCards });
                }}
                className={smallInputClass}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Badge</label>
              <input
                type="text"
                value={card.badge || ''}
                onChange={(e) => {
                  const newCards = [...section.cards];
                  newCards[idx] = { ...card, badge: e.target.value };
                  onChange({ ...section, cards: newCards });
                }}
                className={smallInputClass}
                placeholder="Optional"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Description</label>
            <AutoTextarea
              value={card.description || ''}
              onChange={(e) => {
                const newCards = [...section.cards];
                newCards[idx] = { ...card, description: e.target.value };
                onChange({ ...section, cards: newCards });
              }}
              placeholder="Card description"
              minRows={2}
            />
          </div>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, cards: [...(section.cards || []), { icon: 'shield', title: '', description: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Card
      </button>
    </div>
  </div>
);

// Symptom Cards Editor
const SymptomCardsEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Items</label>
      {(section.items || []).map((item, idx) => (
        <div key={idx} className="flex gap-2 items-start p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex-1 space-y-2">
            <input
              type="text"
              value={item.text || ''}
              onChange={(e) => {
                const newItems = [...section.items];
                newItems[idx] = { ...item, text: e.target.value };
                onChange({ ...section, items: newItems });
              }}
              className={smallInputClass}
              placeholder="Item text"
            />
            <AutoTextarea
              value={item.description || ''}
              onChange={(e) => {
                const newItems = [...section.items];
                newItems[idx] = { ...item, description: e.target.value };
                onChange({ ...section, items: newItems });
              }}
              placeholder="Description (optional)"
              minRows={2}
            />
          </div>
          <button
            onClick={() => {
              const newItems = section.items.filter((_, i) => i !== idx);
              onChange({ ...section, items: newItems });
            }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, items: [...(section.items || []), { text: '', description: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Item
      </button>
    </div>
  </div>
);

// Process Steps Editor
const ProcessStepsEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Steps</label>
      {(section.steps || []).map((step, idx) => (
        <div key={idx} className="flex gap-3 items-start p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-bold flex-shrink-0 mt-1">
            {idx + 1}
          </div>
          <div className="flex-1 space-y-2">
            <input
              type="text"
              value={step.title || ''}
              onChange={(e) => {
                const newSteps = [...section.steps];
                newSteps[idx] = { ...step, title: e.target.value };
                onChange({ ...section, steps: newSteps });
              }}
              className={`${smallInputClass} font-medium`}
              placeholder="Step title"
            />
            <AutoTextarea
              value={step.description || ''}
              onChange={(e) => {
                const newSteps = [...section.steps];
                newSteps[idx] = { ...step, description: e.target.value };
                onChange({ ...section, steps: newSteps });
              }}
              placeholder="Step description"
              minRows={2}
            />
          </div>
          <button
            onClick={() => {
              const newSteps = section.steps.filter((_, i) => i !== idx);
              onChange({ ...section, steps: newSteps });
            }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, steps: [...(section.steps || []), { title: '', description: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Step
      </button>
    </div>
  </div>
);

// Info Box Editor
const InfoBoxEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Box Type</label>
        <select
          value={section.boxType || 'info'}
          onChange={(e) => onChange({ ...section, boxType: e.target.value })}
          className={inputClass}
        >
          <option value="info">Info (Blue)</option>
          <option value="warning">Warning (Yellow)</option>
          <option value="important">Important (Red)</option>
          <option value="success">Success (Green)</option>
        </select>
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
      <AutoTextarea
        value={section.content || ''}
        onChange={(e) => onChange({ ...section, content: e.target.value })}
        placeholder="Info box content..."
        minRows={3}
      />
    </div>
  </div>
);

// Comparison Columns Editor
const ComparisonColumnsEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">Columns</label>
      {(section.columns || []).map((column, colIdx) => (
        <div key={colIdx} className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-600">Column {colIdx + 1}</span>
            <button
              onClick={() => {
                const newColumns = section.columns.filter((_, i) => i !== colIdx);
                onChange({ ...section, columns: newColumns });
              }}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Title</label>
              <input
                type="text"
                value={column.title || ''}
                onChange={(e) => {
                  const newColumns = [...section.columns];
                  newColumns[colIdx] = { ...column, title: e.target.value };
                  onChange({ ...section, columns: newColumns });
                }}
                className={smallInputClass}
                placeholder="e.g., Dogs"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Icon/Emoji</label>
              <input
                type="text"
                value={column.icon || ''}
                onChange={(e) => {
                  const newColumns = [...section.columns];
                  newColumns[colIdx] = { ...column, icon: e.target.value };
                  onChange({ ...section, columns: newColumns });
                }}
                className={smallInputClass}
                placeholder="e.g., 🐕"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-gray-600">Items</label>
            {(column.items || []).map((item, itemIdx) => (
              <div key={itemIdx} className="flex gap-2 items-start p-2 bg-gray-50 rounded border border-gray-100">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => {
                      const newColumns = [...section.columns];
                      const newItems = [...(column.items || [])];
                      newItems[itemIdx] = { ...item, name: e.target.value };
                      newColumns[colIdx] = { ...column, items: newItems };
                      onChange({ ...section, columns: newColumns });
                    }}
                    className={smallInputClass}
                    placeholder="Item name"
                  />
                  <AutoTextarea
                    value={item.description || ''}
                    onChange={(e) => {
                      const newColumns = [...section.columns];
                      const newItems = [...(column.items || [])];
                      newItems[itemIdx] = { ...item, description: e.target.value };
                      newColumns[colIdx] = { ...column, items: newItems };
                      onChange({ ...section, columns: newColumns });
                    }}
                    placeholder="Description"
                    minRows={2}
                  />
                </div>
                <button
                  onClick={() => {
                    const newColumns = [...section.columns];
                    const newItems = (column.items || []).filter((_, i) => i !== itemIdx);
                    newColumns[colIdx] = { ...column, items: newItems };
                    onChange({ ...section, columns: newColumns });
                  }}
                  className="p-1 text-red-400 hover:text-red-600 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const newColumns = [...section.columns];
                newColumns[colIdx] = { ...column, items: [...(column.items || []), { name: '', description: '' }] };
                onChange({ ...section, columns: newColumns });
              }}
              className="w-full py-1.5 text-sm text-blue-600 border border-dashed border-blue-300 rounded hover:bg-blue-50 flex items-center justify-center gap-1"
            >
              <Plus className="h-4 w-4" /> Add Item
            </button>
          </div>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, columns: [...(section.columns || []), { title: '', icon: '', items: [] }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Column
      </button>
    </div>
  </div>
);

// Care Cards Editor
const CareCardsEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Cards</label>
      {(section.cards || []).map((card, idx) => (
        <div key={idx} className="flex gap-2 items-start p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex-1 space-y-2">
            <input
              type="text"
              value={card.title || ''}
              onChange={(e) => {
                const newCards = [...section.cards];
                newCards[idx] = { ...card, title: e.target.value };
                onChange({ ...section, cards: newCards });
              }}
              className={`${smallInputClass} font-medium`}
              placeholder="Card title"
            />
            <AutoTextarea
              value={card.description || ''}
              onChange={(e) => {
                const newCards = [...section.cards];
                newCards[idx] = { ...card, description: e.target.value };
                onChange({ ...section, cards: newCards });
              }}
              placeholder="Card description"
              minRows={2}
            />
          </div>
          <button
            onClick={() => {
              const newCards = section.cards.filter((_, i) => i !== idx);
              onChange({ ...section, cards: newCards });
            }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, cards: [...(section.cards || []), { title: '', description: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Card
      </button>
    </div>
  </div>
);

// Summary Table Editor
const SummaryTableEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
      <input
        type="text"
        value={section.title || ''}
        onChange={(e) => onChange({ ...section, title: e.target.value })}
        className={inputClass}
      />
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Rows</label>
      {(section.rows || []).map((row, idx) => (
        <div key={idx} className="flex gap-2 items-start p-3 bg-white rounded-lg border border-gray-200">
          <div className="w-1/3">
            <label className="block text-sm text-gray-600 mb-1">Category</label>
            <input
              type="text"
              value={row.category || ''}
              onChange={(e) => {
                const newRows = [...section.rows];
                newRows[idx] = { ...row, category: e.target.value };
                onChange({ ...section, rows: newRows });
              }}
              className={`${smallInputClass} font-medium`}
              placeholder="Category"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-gray-600 mb-1">Key Points</label>
            <AutoTextarea
              value={row.points || ''}
              onChange={(e) => {
                const newRows = [...section.rows];
                newRows[idx] = { ...row, points: e.target.value };
                onChange({ ...section, rows: newRows });
              }}
              placeholder="Key points"
              minRows={2}
            />
          </div>
          <button
            onClick={() => {
              const newRows = section.rows.filter((_, i) => i !== idx);
              onChange({ ...section, rows: newRows });
            }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded self-end"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, rows: [...(section.rows || []), { category: '', points: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Row
      </button>
    </div>
  </div>
);

// Feature Grid Editor
const FeatureGridEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Features</label>
      {(section.features || []).map((feature, idx) => (
        <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-600">Feature {idx + 1}</span>
            <button
              onClick={() => {
                const newFeatures = section.features.filter((_, i) => i !== idx);
                onChange({ ...section, features: newFeatures });
              }}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Icon</label>
              <select
                value={feature.icon || 'check-circle'}
                onChange={(e) => {
                  const newFeatures = [...section.features];
                  newFeatures[idx] = { ...feature, icon: e.target.value };
                  onChange({ ...section, features: newFeatures });
                }}
                className={smallInputClass}
              >
                {availableIcons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Title</label>
              <input
                type="text"
                value={feature.title || ''}
                onChange={(e) => {
                  const newFeatures = [...section.features];
                  newFeatures[idx] = { ...feature, title: e.target.value };
                  onChange({ ...section, features: newFeatures });
                }}
                className={smallInputClass}
              />
            </div>
          </div>
          
          {/* Description or Items */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              {feature.items ? 'Bullet Items' : 'Description'}
            </label>
            {feature.items ? (
              <div className="space-y-2">
                {(feature.items || []).map((item, itemIdx) => (
                  <div key={itemIdx} className="flex gap-2 items-center">
                    <span className="text-gray-400">•</span>
                    <input
                      type="text"
                      value={item || ''}
                      onChange={(e) => {
                        const newFeatures = [...section.features];
                        const newItems = [...(feature.items || [])];
                        newItems[itemIdx] = e.target.value;
                        newFeatures[idx] = { ...feature, items: newItems };
                        onChange({ ...section, features: newFeatures });
                      }}
                      className={`flex-1 ${smallInputClass}`}
                      placeholder="Bullet item"
                    />
                    <button
                      onClick={() => {
                        const newFeatures = [...section.features];
                        const newItems = (feature.items || []).filter((_, i) => i !== itemIdx);
                        newFeatures[idx] = { ...feature, items: newItems };
                        onChange({ ...section, features: newFeatures });
                      }}
                      className="p-1 text-red-400 hover:text-red-600 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newFeatures = [...section.features];
                    newFeatures[idx] = { ...feature, items: [...(feature.items || []), ''] };
                    onChange({ ...section, features: newFeatures });
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" /> Add Item
                </button>
              </div>
            ) : (
              <>
                <AutoTextarea
                  value={feature.description || ''}
                  onChange={(e) => {
                    const newFeatures = [...section.features];
                    newFeatures[idx] = { ...feature, description: e.target.value };
                    onChange({ ...section, features: newFeatures });
                  }}
                  placeholder="Feature description"
                  minRows={2}
                />
                <button
                  onClick={() => {
                    const newFeatures = [...section.features];
                    newFeatures[idx] = { ...feature, items: [''], description: undefined };
                    onChange({ ...section, features: newFeatures });
                  }}
                  className="mt-2 text-sm text-gray-500 hover:text-blue-600"
                >
                  Switch to bullet items
                </button>
              </>
            )}
          </div>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, features: [...(section.features || []), { icon: 'check-circle', title: '', description: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Feature
      </button>
    </div>
  </div>
);

// FAQ Editor
const FAQEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          value={section.subtitle || ''}
          onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Questions & Answers</label>
      {(section.faqs || []).map((faq, idx) => (
        <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-600">Q&A {idx + 1}</span>
            <button
              onClick={() => {
                const newFaqs = section.faqs.filter((_, i) => i !== idx);
                onChange({ ...section, faqs: newFaqs });
              }}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-2">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Question</label>
              <input
                type="text"
                value={faq.question || ''}
                onChange={(e) => {
                  const newFaqs = [...section.faqs];
                  newFaqs[idx] = { ...faq, question: e.target.value };
                  onChange({ ...section, faqs: newFaqs });
                }}
                className={`${smallInputClass} font-medium`}
                placeholder="Question"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Answer</label>
              <AutoTextarea
                value={faq.answer || ''}
                onChange={(e) => {
                  const newFaqs = [...section.faqs];
                  newFaqs[idx] = { ...faq, answer: e.target.value };
                  onChange({ ...section, faqs: newFaqs });
                }}
                placeholder="Answer"
                minRows={3}
              />
            </div>
          </div>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, faqs: [...(section.faqs || []), { question: '', answer: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Q&A
      </button>
    </div>
  </div>
);

// Stats Editor
const StatsEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
      <input
        type="text"
        value={section.title || ''}
        onChange={(e) => onChange({ ...section, title: e.target.value })}
        className={inputClass}
      />
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Statistics</label>
      <div className="grid grid-cols-2 gap-3">
        {(section.stats || []).map((stat, idx) => (
          <div key={idx} className="flex gap-2 items-start p-3 bg-white rounded-lg border border-gray-200">
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={stat.value || ''}
                onChange={(e) => {
                  const newStats = [...section.stats];
                  newStats[idx] = { ...stat, value: e.target.value };
                  onChange({ ...section, stats: newStats });
                }}
                className={`${smallInputClass} font-bold text-center`}
                placeholder="e.g., 80%"
              />
              <input
                type="text"
                value={stat.label || ''}
                onChange={(e) => {
                  const newStats = [...section.stats];
                  newStats[idx] = { ...stat, label: e.target.value };
                  onChange({ ...section, stats: newStats });
                }}
                className={`${smallInputClass} text-center`}
                placeholder="Label"
              />
            </div>
            <button
              onClick={() => {
                const newStats = section.stats.filter((_, i) => i !== idx);
                onChange({ ...section, stats: newStats });
              }}
              className="p-1 text-red-400 hover:text-red-600 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={() => onChange({ ...section, stats: [...(section.stats || []), { value: '', label: '' }] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Stat
      </button>
    </div>
  </div>
);

// Bullet List Editor
const BulletListEditor = ({ section, onChange }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
        <select
          value={section.icon || 'check-circle'}
          onChange={(e) => onChange({ ...section, icon: e.target.value })}
          className={inputClass}
        >
          {availableIcons.map(icon => (
            <option key={icon} value={icon}>{icon}</option>
          ))}
        </select>
      </div>
    </div>
    
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Items</label>
      {(section.items || []).map((item, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <span className="text-gray-400">•</span>
          <input
            type="text"
            value={typeof item === 'string' ? item : item.text || ''}
            onChange={(e) => {
              const newItems = [...section.items];
              newItems[idx] = e.target.value;
              onChange({ ...section, items: newItems });
            }}
            className={`flex-1 ${smallInputClass}`}
            placeholder="List item"
          />
          <button
            onClick={() => {
              const newItems = section.items.filter((_, i) => i !== idx);
              onChange({ ...section, items: newItems });
            }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange({ ...section, items: [...(section.items || []), ''] })}
        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1"
      >
        <Plus className="h-4 w-4" /> Add Item
      </button>
    </div>
  </div>
);

// Get editor component for section type
const getSectionEditor = (type) => {
  switch (type) {
    case 'text-block': return TextBlockEditor;
    case 'image-text': return ImageTextEditor;
    case 'benefit-cards': return BenefitCardsEditor;
    case 'symptom-cards': return SymptomCardsEditor;
    case 'process-steps': return ProcessStepsEditor;
    case 'info-box': return InfoBoxEditor;
    case 'comparison-columns': return ComparisonColumnsEditor;
    case 'care-cards': return CareCardsEditor;
    case 'summary-table': return SummaryTableEditor;
    case 'feature-grid': return FeatureGridEditor;
    case 'faq': return FAQEditor;
    case 'stats': return StatsEditor;
    case 'bullet-list': return BulletListEditor;
    default: return null;
  }
};

// Main Component
const InlineStructuredContentEditor = ({ content, onChange }) => {
  // Parse content
  const parsedContent = React.useMemo(() => {
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      return parsed && parsed.sections ? parsed : { sections: [] };
    } catch {
      return { sections: [] };
    }
  }, [content]);

  const [sections, setSections] = React.useState(parsedContent.sections);
  const [expandedSection, setExpandedSection] = React.useState(null);
  const [showAddMenu, setShowAddMenu] = React.useState(false);

  // Sync sections when content changes externally
  React.useEffect(() => {
    setSections(parsedContent.sections);
  }, [parsedContent]);

  // Update parent when sections change
  const updateSections = (newSections) => {
    setSections(newSections);
    onChange(JSON.stringify({ sections: newSections }));
  };

  const handleSectionChange = (index, updatedSection) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    updateSections(newSections);
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    updateSections(newSections);
  };

  const deleteSection = (index) => {
    const newSections = sections.filter((_, i) => i !== index);
    updateSections(newSections);
    if (expandedSection === index) setExpandedSection(null);
  };

  const addSection = (type) => {
    const newSection = { type };
    switch (type) {
      case 'text-block':
        newSection.title = '';
        newSection.paragraphs = [''];
        break;
      case 'image-text':
        newSection.title = '';
        newSection.content = [''];
        newSection.image = '';
        newSection.imageAlt = '';
        newSection.imagePosition = 'right';
        newSection.bullets = [];
        break;
      case 'benefit-cards':
      case 'care-cards':
        newSection.title = '';
        newSection.cards = [];
        break;
      case 'symptom-cards':
        newSection.title = '';
        newSection.items = [];
        break;
      case 'process-steps':
        newSection.title = '';
        newSection.steps = [];
        break;
      case 'info-box':
        newSection.title = '';
        newSection.content = '';
        newSection.boxType = 'info';
        break;
      case 'comparison-columns':
        newSection.title = '';
        newSection.columns = [];
        break;
      case 'summary-table':
        newSection.title = '';
        newSection.rows = [];
        break;
      case 'feature-grid':
        newSection.title = '';
        newSection.features = [];
        break;
      case 'faq':
        newSection.title = '';
        newSection.faqs = [];
        break;
      case 'stats':
        newSection.title = '';
        newSection.stats = [];
        break;
      case 'bullet-list':
        newSection.title = '';
        newSection.items = [];
        newSection.icon = 'check-circle';
        break;
      default:
        break;
    }
    const newSections = [...sections, newSection];
    updateSections(newSections);
    setExpandedSection(sections.length);
    setShowAddMenu(false);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        AI-generated structured content. Click each section to expand and edit.
      </p>
      
      {/* Sections List */}
      <div className="space-y-2">
        {sections.map((section, index) => {
          const typeInfo = sectionTypes[section.type] || { icon: FileText, label: section.type, color: 'bg-gray-100 text-gray-700 border-gray-200' };
          const IconComponent = typeInfo.icon;
          const SectionEditor = getSectionEditor(section.type);
          const isExpanded = expandedSection === index;

          return (
            <div
              key={index}
              className={`border rounded-lg overflow-hidden transition-all ${isExpanded ? 'border-blue-300 shadow-sm' : 'border-gray-200'}`}
            >
              {/* Section Header */}
              <div
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'}`}
                onClick={() => setExpandedSection(isExpanded ? null : index)}
              >
                <div className={`flex items-center justify-center w-6 h-6 rounded ${typeInfo.color}`}>
                  <IconComponent className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate block">
                    {section.title || `${typeInfo.label}`}
                  </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${typeInfo.color} border`}>
                  {typeInfo.label}
                </span>
                <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                  {index > 0 && (
                    <button
                      onClick={() => moveSection(index, 'up')}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {index < sections.length - 1 && (
                    <button
                      onClick={() => moveSection(index, 'down')}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteSection(index)}
                    className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Section Editor (Expanded) */}
              {isExpanded && (
                <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
                  {SectionEditor ? (
                    <SectionEditor
                      section={section}
                      onChange={(updated) => handleSectionChange(index, updated)}
                    />
                  ) : (
                    <div className="p-4 bg-yellow-50 rounded-lg text-yellow-700 text-sm">
                      Visual editor not available for this section type.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Section Button */}
      <div className="relative">
        <button
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="w-full py-2 text-sm text-blue-600 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 flex items-center justify-center gap-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Section
        </button>
        
        {showAddMenu && (
          <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 grid grid-cols-3 gap-1">
            {Object.entries(sectionTypes).map(([type, info]) => {
              const IconComponent = info.icon;
              return (
                <button
                  key={type}
                  onClick={() => addSection(type)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 text-sm rounded transition-colors ${info.color} hover:opacity-80`}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="truncate">{info.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineStructuredContentEditor;
